import { Box, Typography, useTheme } from "@mui/material";
import { useLenis } from "src/context/LenisContext";
import { useEffect, useState } from "react";

type PropsType = {
  sectionHeights: number[];
};

const Sidebar: React.FC<PropsType> = ({ sectionHeights }) => {
  const theme = useTheme();
  const [barHeightPx, setBarHeightPx] = useState(0);
  const [translateYPx, setTranslateYPx] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const lenis = useLenis();

  useEffect(() => {
    const handleScrollOrResize = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const trackHeight = vh - vw * 0.0625;

      const total = document.documentElement.scrollHeight;
      const scrollY = window.scrollY;

      const barHeight = (vh / total) * trackHeight;
      const maxTranslate = trackHeight - barHeight;
      const scrollRatio = scrollY / (total - vh);
      const translateY = scrollRatio * maxTranslate;

      setBarHeightPx(barHeight);
      setTranslateYPx(translateY);
      let sum = 0;
      for (let i = 0; i < sectionHeights.length; i++) {
        sum += sectionHeights[i];
        if (scrollY < sum - vh / 2) {
          setActiveIndex(i);
          break;
        }
      }
    };

    handleScrollOrResize();
    window.addEventListener("scroll", handleScrollOrResize);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [sectionHeights]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startY = e.clientY;
    const startScroll = window.scrollY;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const trackHeight = vh - vw * 0.0625;
    const total = document.documentElement.scrollHeight;
    const scrollable = total - vh;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const ratio = deltaY / trackHeight;
      const newScroll = startScroll + ratio * scrollable;
      window.scrollTo({ top: newScroll });
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const totalHeight = sectionHeights.reduce((a, b) => a + (b || 0), 0) || 1;

  const positions = sectionHeights.map((_, i) => {
    const prevSum = sectionHeights.slice(0, i).reduce((a, b) => a + (b || 0), 0);
    return (prevSum / totalHeight) * 100;
  });

  const scrollToSection = (index: number) => {
    lenis?.scrollTo(`#section-${index}`);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "none",
          [theme.breakpoints.up("lg")]: {
            position: "fixed",
            height: "calc(100vh - 6.25vw)",
            display: "flex",
            bottom: "1.042vw",
            right: "1.042vw",
            zIndex: 99,
          },
        }}
      >
        <Box
          sx={{ height: "93.4749%", pr: ".521vw", display: "flex", position: "absolute", top: 0, transition: "opacity .3s ease-in-out" }}
        >
          {positions.map((top, idx) => (
            <Box
              key={idx}
              sx={{
                position: "absolute",
                top: `${top}%`,
                right: "20px",
                pointerEvents: "all",
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <Typography sx={{ color: "#9ba5b7f5", opacity: hoverIdx === idx ? 1 : 0, transition: "opacity 0.3s ease-in-out" }}>
                welcome
              </Typography>
              <Typography
                onClick={() => scrollToSection(idx)}
                onMouseEnter={() => setHoverIdx(idx)}
                onMouseLeave={() => setHoverIdx(null)}
                sx={{
                  color: activeIndex === idx || hoverIdx === idx ? "#9ba5b7f5" : "#8695ae38",
                  letterSpacing: ".04em",
                  minWidth: "24px",
                  fontSize: "clamp(14px,.833vw,16px)",
                  fontWeight: "500",
                  lineHeight: 1.4,
                  transition: "color .3s ease-in-out",
                  cursor: "pointer",
                }}
              >
                0{idx + 1}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: "none",
          [theme.breakpoints.up("lg")]: {
            display: "block",
            position: "fixed",
            opacity: 1,
            zIndex: 9999,
            width: "4px",
            bottom: "1.042vw",
            right: "1.042vw",
            height: "calc(100vh - 6.25vw)",
            borderRadius: "20px",
            bgcolor: "#8695ae38",
            transition: "transform .3s,opacity .3s,width .3s",
          },
        }}
      >
        <Box
          sx={{
            height: `${barHeightPx}px`,
            transform: `translateY(${translateYPx}px)`,
            background: "#9ba5b7f5",
            borderRadius: "20px",
            width: "100%",
            transition: "transform .1s linear",
            position: "absolute",
            top: 0,
            zIndex: 9999,
            cursor: "pointer",
          }}
          onMouseDown={e => handleMouseDown(e)}
        />
      </Box>
    </Box>
  );
};

export default Sidebar;
